import { Box, BoxProps } from "@material-ui/core";
import { SignatureNameTag, SignatureNameTagColor } from "../../../@types/signatures";

// ----------------------------------------------------------------------

/**
 * Pastel palette for the template name-tag. Each colour key maps to a light
 * background with readable dark-ish text and a slightly darker border. Kept in
 * the dashboard (not the shared schema) so the palette can be tuned without a
 * data migration — the persisted value is just the stable colour KEY.
 */
export const NAME_TAG_PALETTE: Record<
  SignatureNameTagColor,
  { bg: string; text: string; border: string }
> = {
  slate: { bg: "#e2e8f0", text: "#475569", border: "#cbd5e1" },
  indigo: { bg: "#e0e7ff", text: "#4338ca", border: "#c7d2fe" },
  sky: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" },
  teal: { bg: "#ccfbf1", text: "#0f766e", border: "#99f6e4" },
  green: { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" },
  amber: { bg: "#fef3c7", text: "#b45309", border: "#fde68a" },
  rose: { bg: "#ffe4e6", text: "#be123c", border: "#fecdd3" },
  purple: { bg: "#f3e8ff", text: "#7e22ce", border: "#e9d5ff" }
};

const FALLBACK_PALETTE = NAME_TAG_PALETTE.indigo;

export function nameTagPalette(color: SignatureNameTagColor) {
  return NAME_TAG_PALETTE[color] || FALLBACK_PALETTE;
}

export type TemplateNameTagProps = {
  nameTag?: SignatureNameTag | null;
  sx?: BoxProps["sx"];
};

/**
 * Small pastel badge shown immediately before a signature template's name
 * across the dashboard (marketplace, template lists, pickers). Renders nothing
 * when the template has no tag. Renders inline (inline-flex) so it can be
 * dropped straight inside an existing Typography / flex row right before the
 * name — `marginInlineEnd` keeps the gap on the name side in both LTR and RTL.
 */
export default function TemplateNameTag({ nameTag, sx }: TemplateNameTagProps) {
  const label = nameTag?.label?.trim();
  if (!label) return null;
  const palette = nameTagPalette(nameTag!.color);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        flexShrink: 0,
        height: 18,
        px: 0.75,
        borderRadius: "6px",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
        color: palette.text,
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        marginInlineEnd: "6px",
        ...sx
      }}
    >
      {label}
    </Box>
  );
}
